/**
 * NWF-126: colour picker with code search.
 *
 * Filters the already rendered rows client side, keeps a quantity per colour
 * and adds every picked colour in one request. Hooks the product form in the
 * capture phase so the existing buy button stays in charge of the layout while
 * this element owns the submission.
 */
class NwfColorPicker extends HTMLElement {
    /** Per-row ceiling when stock does not cap the row; mirrors the input's max in the Liquid. */
    static MAX_PER_ROW = 99;

    constructor() {
        super();
        this.rows = Array.from(this.querySelectorAll('[data-row]'));
        this.searchInput = this.querySelector('[data-search-input]');
        this.summary = this.querySelector('[data-summary]');
        this.total = this.querySelector('[data-total]');
        this.empty = this.querySelector('[data-empty]');
        this.errorBox = this.querySelector('[data-error]');
        this.resetButton = this.querySelector('[data-reset]');
        this.searchTimer = null;

        // Preview overlay: one dialog for the whole picker, filled per row.
        this.previewDialog = this.querySelector('[data-preview-dialog]');
        this.previewImage = this.querySelector('[data-preview-image-target]');
        this.previewCaption = this.querySelector('[data-preview-caption]');
        this.previewCloseButton = this.querySelector('[data-preview-close]');
        this.previewTrigger = null;
    }

    connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);

        // NWF-126: the sticky bar carries no inputs while this picker is
        // active, its button scrolls here instead of submitting.
        document.addEventListener('click', this.onScrollToPickerClick);

        // The tier block is rendered by nwf-price.liquid, outside this element,
        // and only for products that carry the metafield - hence the lookup in
        // the document and the empty defaults for products without tiers.
        this.tierBlock = document.querySelector('[data-nwf-tiers]');
        this.tierRows = Array.from(this.tierBlock?.querySelectorAll('[data-nwf-tier]') || []);
        this.tierBadge = this.tierBlock?.querySelector('[data-nwf-tier-badge]');
        this.cartQuantity = parseInt(this.tierBlock?.dataset.cartQuantity || '0', 10) || 0;

        this.searchInput?.addEventListener('input', this.onSearchInput);
        this.resetButton?.addEventListener('click', this.onReset);
        this.addEventListener('change', this.onQuantityChange);
        this.addEventListener('click', this.onQuantityClick);
        this.addEventListener('click', this.onPreviewOpenClick);
        this.previewCloseButton?.addEventListener('click', this.closePreview);
        this.previewDialog?.addEventListener('click', this.onPreviewDialogClick);
        this.previewDialog?.addEventListener('cancel', this.onPreviewCancel);

        // Capture phase: stop the theme's single variant submit before it runs.
        this.form?.addEventListener('submit', this.onSubmit, true);

        // NWF-167: Meterware length changes move the per-row stock maximum.
        // The selector's own handlers run first (target phase), these fire on
        // the bubble with the hidden _length_cm already updated.
        this.meterwareSelectorNode = this.meterwareSelector;
        ['input', 'change', 'click'].forEach((type) => {
            this.meterwareSelectorNode?.addEventListener(type, this.onMeterwareChange);
        });

        // The tier state depends on what the cart holds for this product, so it
        // has to follow every cart change, not just our own adds - removing the
        // product in the drawer has to drop the active tier again.
        this.cartUnsubscriber = window.FoxTheme?.pubsub?.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.onCartUpdate,
        );
        document.addEventListener('cart:refresh', this.onCartRefresh);

        this.updateSummary();
    }

    onCartUpdate = (event) => {
        const cart = event?.cart;
        if (cart?.items) {
            this.readCartQuantity(cart);
            this.updateSummary();
            return;
        }
        this.onCartRefresh();
    };

    /** Some cart events carry no payload, then the cart has to be read again. */
    onCartRefresh = async () => {
        try {
            const cart = await (
                await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
            ).json();
            this.readCartQuantity(cart);
            this.updateSummary();
        } catch (error) {
            console.error(error);
        }
    };

    disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit, true);
        ['input', 'change', 'click'].forEach((type) => {
            this.meterwareSelectorNode?.removeEventListener(type, this.onMeterwareChange);
        });
        document.removeEventListener('click', this.onScrollToPickerClick);
        document.removeEventListener('cart:refresh', this.onCartRefresh);
        this.cartUnsubscriber?.();
    }

    onScrollToPickerClick = (event) => {
        const button = event.target.closest('[data-nwf-scroll-to-picker]');
        if (!button) return;

        event.preventDefault();
        // The sticky bar's own handler puts the button into a loading state on
        // click and only resets it on a cart event, which never comes here.
        button.classList.remove('btn--loading');
        button.removeAttribute('aria-disabled');
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus after the scroll so the browser does not jump twice.
        window.setTimeout(() => this.searchInput?.focus({ preventScroll: true }), 500);
    };

    get quantityInputs() {
        return this.querySelectorAll('.nwf-color-row__quantity input');
    }

    /** Rows with a quantity above zero, in the order they appear. */
    get selection() {
        return this.rows
            .map((row) => ({
                row,
                id: row.dataset.variantId,
                label: row.dataset.label,
                quantity: parseInt(row.querySelector('input')?.value || '0', 10) || 0,
            }))
            .filter((entry) => entry.quantity > 0 && entry.id);
    }

    onSearchInput = () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(this.applyFilter, 200);
    };

    applyFilter = () => {
        const term = this.normalise(this.searchInput.value);
        let visible = 0;

        this.rows.forEach((row) => {
            const match = term === '' || this.normalise(row.dataset.search).includes(term);
            row.hidden = !match;
            if (match) visible += 1;
        });

        if (this.empty) this.empty.hidden = visible > 0;
    };

    /**
     * Codes are written in many ways: "1000 Schwarz", "1000-schwarz", "1000".
     * Comparing without separators and without leading zeros makes all of them
     * find the same colour.
     */
    normalise(value) {
        return (value || '')
            .toString()
            .toLowerCase()
            .replace(/[\s\-_.]/g, '')
            .replace(/^0+/, '');
    }

    onQuantityChange = (event) => {
        if (!event.target.matches('input')) return;
        this.clampRow(event.target);
        this.updateSummary();
    };

    /**
     * The picker steps the amount itself instead of leaning on the theme
     * control: theme.js registers quantity-input as a standalone custom element
     * while the theme markup uses it as `is="quantity-input"`, so the theme
     * buttons never receive a handler.
     */
    onQuantityClick = (event) => {
        const button = event.target.closest('[data-quantity-step]');
        if (!button) return;

        const input = button.closest('[data-row]')?.querySelector('input');
        if (!input) return;

        const step = parseInt(button.dataset.quantityStep, 10) || 0;
        const current = parseInt(input.value || '0', 10) || 0;
        const min = parseInt(input.min || '0', 10) || 0;

        input.value = Math.max(min, current + step);
        this.clampRow(input);
        this.updateSummary();
    };

    /**
     * The swatch only enlarges the photo: the click must not step the quantity
     * and must not select the row, so it is stopped right here and never
     * reaches the form.
     */
    onPreviewOpenClick = (event) => {
        const trigger = event.target.closest('[data-preview-open]');
        if (!trigger || !this.previewDialog) return;

        event.preventDefault();
        event.stopPropagation();

        this.previewTrigger = trigger;
        const label = trigger.dataset.previewLabel || '';

        if (this.previewImage) {
            this.previewImage.src = trigger.dataset.previewImage || '';
            this.previewImage.alt = label;
        }
        if (this.previewCaption) this.previewCaption.textContent = label;

        this.previewDialog.showModal();
        // showModal already moves the focus into the dialog; naming the close
        // button makes the landing spot predictable across browsers.
        this.previewCloseButton?.focus();
    };

    /** Backdrop click: a modal dialog reports those on the dialog itself. */
    onPreviewDialogClick = (event) => {
        if (event.target === this.previewDialog) this.closePreview();
    };

    /**
     * Escape. The browser would close the dialog on its own, but its `close`
     * event is queued asynchronously - routing the key through closePreview
     * keeps all three ways out on the very same, synchronous path.
     */
    onPreviewCancel = (event) => {
        event.preventDefault();
        this.closePreview();
    };

    /** Close button, backdrop and Escape all end up here. */
    closePreview = () => {
        if (!this.previewDialog?.open) return;

        this.previewDialog.close();
        // Dropping the source keeps the closed dialog from holding on to the
        // large image, and the next open always starts from a blank frame.
        this.previewImage?.removeAttribute('src');
        // Focus can only leave the dialog once it is closed.
        this.previewTrigger?.focus();
        this.previewTrigger = null;
    };

    clampRow(input) {
        const row = input.closest('[data-row]');
        const value = parseInt(input.value || '0', 10);

        if (Number.isNaN(value) || value < 0) {
            input.value = 0;
            return;
        }
        if (row?.dataset.available === 'false' && value > 0) {
            input.value = 0;
            this.showError(this.errorBox?.dataset.soldOutMessage || '');
            this.keepError = true;
            return;
        }

        // NWF-167: without overselling a colour cannot be picked beyond its stock.
        const max = this.maxQuantity(row);
        if (max !== null && value > max) {
            input.value = max;
            // max 0 only happens for Meterware: stock left, but less than one cut.
            const template =
                max === 0 ? this.errorBox?.dataset.lengthMessage || '' : this.errorBox?.dataset.maxMessage || '';
            // A stock limit is a hint, not a mistake the shopper made - amber, not red.
            // showNotice escapes the whole string and keeps only the locale's <strong>.
            this.showNotice(template.replace('[color]', row.dataset.label || '').replace('[count]', max));
            this.keepError = true;
            return;
        }

        // Untracked stock or overselling: still a ceiling, since buildItems posts
        // one line per Meterware cut and a stray "9999" would freeze the request.
        if (value > NwfColorPicker.MAX_PER_ROW) {
            input.value = NwfColorPicker.MAX_PER_ROW;
            this.showNotice((this.errorBox?.dataset.capMessage || '').replace('[count]', NwfColorPicker.MAX_PER_ROW));
            this.keepError = true;
        }
    }

    /**
     * Sellable quantity of a row, or null when Shopify does not track the
     * variant's stock or allows overselling (empty data-max, see the Liquid).
     *
     * Stock is counted in units. For Meterware one unit is one length step
     * and one picked cut takes `length / step` units, so the row maximum is
     * the number of whole cuts of the currently chosen length that fit. The
     * length can change after a quantity was entered - see onMeterwareChange.
     */
    maxQuantity(row) {
        if (!row || row.dataset.max === undefined || row.dataset.max === '') return null;

        const units = parseInt(row.dataset.max, 10);
        if (Number.isNaN(units)) return null;

        const perCut = this.meterwareUnitsPerCut();
        return perCut > 1 ? Math.floor(units / perCut) : units;
    }

    meterwareUnitsPerCut() {
        const selector = this.meterwareSelector;
        const step = parseInt(selector?.dataset.step || '0', 10) || 0;
        const lengthCm = parseInt(selector?.querySelector('[data-nwf-length-cm]')?.value || '0', 10) || 0;

        if (step <= 0 || lengthCm <= 0) return 1;
        return Math.max(1, Math.round(lengthCm / step));
    }

    get meterwareSelector() {
        return document.querySelector(`nwf-meterware-selector[data-form-id="${this.dataset.formId}"]`);
    }

    /** A longer cut fits fewer times into the same stock: re-clamp every row. */
    onMeterwareChange = () => {
        this.quantityInputs.forEach((input) => {
            if ((parseInt(input.value || '0', 10) || 0) > 0) this.clampRow(input);
        });
        this.updateSummary();
    };

    updateSummary() {
        const selection = this.selection;
        const items = selection.reduce((sum, entry) => sum + entry.quantity, 0);

        this.rows.forEach((row) => {
            const quantity = parseInt(row.querySelector('input')?.value || '0', 10) || 0;
            row.classList.toggle('is-selected', quantity > 0);
        });

        if (this.total) {
            const template = this.total.dataset.totalLabel || '';
            this.total.textContent = template.replace('[count]', items);
        }

        if (this.summary) {
            const selected = selection.length;
            this.summary.textContent =
                selected > 0
                    ? (this.summary.dataset.selectedLabel || '')
                          .replace('[selected]', selected)
                          .replace('[count]', this.dataset.count)
                    : this.summary.dataset.countLabel || '';
        }

        this.updateTiers(items);

        // A clamp that just raised an error must survive this refresh, otherwise
        // the message would be hidden in the same tick it was shown.
        if (this.keepError) {
            this.keepError = false;
        } else {
            this.hideError();
        }
    }

    /**
     * NWF-126: mark the tier the shopper has reached. The reference quantity is
     * what the picker holds plus what this product already has in the cart,
     * because the discount Function works on the cart total - somebody who is
     * halfway to a tier has to see the rest of the way. Active is the highest
     * tier whose minimum is met; without a selection and without a cart
     * quantity no tier is active and the badge stays empty, which hides it.
     */
    updateTiers(items) {
        if (this.tierRows.length === 0) return;

        const quantity = items + this.cartQuantity;
        const reached = this.tierRows.filter((row) => {
            const minimum = parseInt(row.dataset.tierQuantity || '0', 10) || 0;
            return minimum > 0 && quantity >= minimum;
        });
        const active = reached.reduce(
            (best, row) =>
                best === null || parseInt(row.dataset.tierQuantity, 10) > parseInt(best.dataset.tierQuantity, 10)
                    ? row
                    : best,
            null,
        );

        this.tierRows.forEach((row) => row.classList.toggle('is-active', row === active));

        if (!this.tierBadge) return;
        const template = this.tierBadge.dataset.activeLabel || '';
        this.tierBadge.textContent = active ? template.replace('[price]', active.dataset.tierPrice) : '';
    }

    /**
     * After a successful add the picker resets, so the highlight would fall
     * back to the cart quantity Liquid rendered before the add. Re-read it from
     * the cart the request just returned instead.
     */
    readCartQuantity(cart) {
        const productId = this.tierBlock?.dataset.productId;
        if (!productId) return;

        const stock = variantStock();
        this.cartQuantity = (cart.items || [])
            .filter((item) => String(item.product_id) === String(productId))
            .reduce((sum, item) => sum + buyableQuantity(item, stock), 0);
    }

    onReset = () => {
        this.quantityInputs.forEach((input) => {
            input.value = 0;
        });
        this.updateSummary();
    };

    onSubmit = (event) => {
        const selection = this.selection;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (selection.length === 0) {
            this.showError(this.errorBox?.dataset.emptyMessage || '');
            this.searchInput?.focus();
            return;
        }

        this.addToCart(selection);
    };

    /**
     * NWF-167: the picker used to post only id + quantity, so on a Meterware
     * product the length chosen in the selector never reached the cart. The
     * two picked cuts of one colour then collapsed into a single line with
     * quantity 2 and no length, which the cart rendered as one minimum cut.
     *
     * Meterware follows the one-cut-per-line model of nwf-meterware.js: the
     * chosen colour quantity is the number of cuts, so every cut becomes its
     * own line with quantity 1, the length properties of the selector and a
     * unique `_cut` so identical cuts stay separate lines. Without a selector
     * on the page the items go out unchanged.
     */
    buildItems(selection) {
        const properties = this.meterwareProperties();

        if (!properties) {
            return selection.map((entry) => ({ id: entry.id, quantity: entry.quantity }));
        }

        const items = [];
        selection.forEach((entry) => {
            for (let cut = 0; cut < entry.quantity; cut += 1) {
                items.push({
                    id: entry.id,
                    quantity: 1,
                    properties: { ...properties, _cut: this.generateCutId() },
                });
            }
        });
        return items;
    }

    /**
     * The selector lives in the price block, outside the form, and hands its
     * line-item properties to the form via the `form` attribute. Read the same
     * hidden inputs the native submit would send, so both paths always agree
     * on the length. Returns null for non-Meterware products.
     */
    meterwareProperties() {
        const selector = this.meterwareSelector;
        const lengthLabel = selector?.querySelector('[data-nwf-length-prop]')?.value;
        const lengthCm = selector?.querySelector('[data-nwf-length-cm]')?.value;

        if (!lengthLabel || !lengthCm) return null;

        return { Länge: lengthLabel, _length_cm: lengthCm };
    }

    generateCutId() {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
            return window.crypto.randomUUID();
        }
        return `cut-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }

    async addToCart(selection) {
        const submitButton = this.form?.querySelector('[type="submit"]');
        submitButton?.setAttribute('aria-disabled', 'true');
        submitButton?.classList.add('btn--loading');

        const sections = [];
        document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }),
        );

        const body = new FormData();
        this.buildItems(selection).forEach((item, index) => {
            body.append(`items[${index}][id]`, item.id);
            body.append(`items[${index}][quantity]`, item.quantity);
            Object.keys(item.properties || {}).forEach((key) => {
                body.append(`items[${index}][properties][${key}]`, item.properties[key]);
            });
        });
        body.append('sections', sections);
        body.append('sections_url', window.location.pathname);

        const config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = body;

        try {
            const response = await fetch(FoxTheme.routes.cart_add_url, config);
            const parsed = await response.json();

            if (parsed.status) {
                this.showError(parsed.description || parsed.message);
                return;
            }

            const cart = await (
                await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
            ).json();
            cart.sections = parsed.sections;

            this.reportShortfall(selection, cart);
            this.readCartQuantity(cart);

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
            this.onReset();
        } catch (error) {
            console.error(error);
            this.showError(this.errorBox?.dataset.genericMessage || '');
        } finally {
            submitButton?.removeAttribute('aria-disabled');
            submitButton?.classList.remove('btn--loading');
        }
    }

    /**
     * Shopify silently reduces a line to the available stock instead of
     * rejecting the request, so compare what was asked for with what the cart
     * now holds and name every colour that came out short.
     */
    reportShortfall(selection, cart) {
        // Summed per variant: a Meterware colour spans one line per cut (NWF-167),
        // so a single line's quantity would always come out short.
        const inCart = new Map();
        (cart.items || []).forEach((item) => {
            const key = String(item.variant_id);
            inCart.set(key, (inCart.get(key) || 0) + item.quantity);
        });
        const short = selection.filter((entry) => (inCart.get(String(entry.id)) || 0) < entry.quantity);

        if (short.length === 0) return;

        const template = this.errorBox?.dataset.shortMessage || '';
        this.showError(template.replace('[colors]', short.map((entry) => entry.label).join(', ')));
    }

    showError(message) {
        if (!this.errorBox || !message) return;
        this.errorBox.classList.remove('is-notice');
        this.errorBox.textContent = message;
        this.errorBox.hidden = false;
    }

    /**
     * Same box, calmer tone: for limits the shopper ran into rather than
     * errors. The template comes from a merchant-editable locale string, so
     * everything is escaped and only the one intended tag, <strong>, is let
     * back through - stray markup in a translation stays visible text.
     */
    showNotice(html) {
        if (!this.errorBox || !html) return;
        this.errorBox.classList.add('is-notice');
        this.errorBox.innerHTML = this.escapeHtml(html).replace(/&lt;(\/?)strong&gt;/g, '<$1strong>');
        this.errorBox.hidden = false;
    }

    escapeHtml(value) {
        const node = document.createElement('span');
        node.textContent = value;
        return node.innerHTML;
    }

    hideError() {
        if (!this.errorBox) return;
        this.errorBox.hidden = true;
        this.errorBox.classList.remove('is-notice');
    }
}

/**
 * NWF-168: the remaining stock per variant, as nwf-cart-staffel.liquid published it.
 *
 * `/cart.js` carries no availability per item — only Liquid sees `variant.available` — so the
 * numbers are read from the same JSON payload that nwf-cart-staffel.js writes into the
 * `_nwf_buyable` cart attribute. Reading the payload rather than the attribute keeps this in
 * step with what Liquid rendered even before the write has landed.
 *
 * An empty map is valid, and so is a missing node: the cart sections render the payload, and a
 * shop configured with the cart as a page has no drawer on a product page.
 */
function variantStock() {
    const node = document.querySelector('[data-nwf-staffel-stock]');
    if (!node) return {};
    try {
        const parsed = JSON.parse(node.textContent);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (error) {
        return {};
    }
}

/**
 * How many units of a cart line count towards a Staffelpreis.
 *
 * A variant the map does not mention counts in full — the same fallback the Functions use: absent
 * is indistinguishable from "nothing has looked yet", and silently dropping quantity would take a
 * tier away from a shopper who has earned it. A variant spanning several lines (Meterware cuts one
 * line per length) caps each line against the same remaining stock, which over-counts rather than
 * under-counts, and that is the milder failure.
 */
function buyableQuantity(item, stock) {
    const remaining = parseInt(stock[String(item.variant_id)], 10);
    if (Number.isNaN(remaining) || remaining < 0) return item.quantity;

    return Math.min(remaining, item.quantity);
}

if (!customElements.get('nwf-color-picker')) {
    customElements.define('nwf-color-picker', NwfColorPicker);
}
